import { QueryClient, QueryFunction } from "@tanstack/react-query";

// ── Typed API error ────────────────────────────────────────────────────────────
// Replaces the plain Error thrown by throwIfResNotOk so callers can inspect
// status, endpoint, and body without parsing the message string.
export class ApiError extends Error {
  readonly status: number;
  readonly endpoint: string;
  readonly body: string;

  constructor(status: number, endpoint: string, body: string) {
    super(`${status}: ${body}`);
    this.name = 'ApiError';
    this.status = status;
    this.endpoint = endpoint;
    this.body = body;
  }

  /** 401 — authentication unavailable or invalid */
  get isAuthError(): boolean { return this.status === 401; }
  /** 403 — caller is not the authorized commissioner for this action */
  get isOwnershipError(): boolean { return this.status === 403; }
  /** 404 / 410 — round or resource not found */
  get isNotFound(): boolean { return this.status === 404 || this.status === 410; }
  /** 5xx / 408 / 429 — safe to back off and retry */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 408 || this.status === 429;
  }
}

async function throwIfResNotOk(res: Response, endpoint: string): Promise<void> {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new ApiError(res.status, endpoint, text);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res, url);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res, queryKey.join("/") as string);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
